import { nanoid } from 'nanoid';
import { PHASE_COLOURS } from './palette';
import { isIsoDay, addDays, DEFAULT_SPRINT_LENGTH, MIN_SPRINT_LENGTH, MAX_SPRINT_LENGTH, dateToSlot } from './dates';

// Schema history
// v1: flat "segments" inside roadmap.data (migrated by migrateLegacyRoadmap)
// v2: projects with blocks on half-sprint slots
// v3: additive only. Every new field is optional and defaults to the v2 behaviour, so v2 files,
//     backups and share links load unchanged, and a v2 copy of the app can still read v3 data
//     (it simply ignores the fields it does not know).
export const SCHEMA_VERSION = 3;
export const SLOTS_PER_SPRINT = 2;
export const DEFAULT_SPRINT_COUNT = 6;
export const MIN_SPRINT_COUNT = 1;
export const MAX_SPRINT_COUNT = 52;

export const DEFAULT_CAPACITY = 100;
export const DEFAULT_ALLOCATION = 50;
export const MAX_BASELINES = 20;

export const STATUSES = [
  { value: 'committed', label: 'Committed', hint: 'Agreed and resourced' },
  { value: 'likely', label: 'Likely', hint: 'Expected, not locked in' },
  { value: 'exploratory', label: 'Exploratory', hint: 'An option we are considering' }
];
const STATUS_VALUES = new Set(STATUSES.map((s) => s.value));

export const uid = () => nanoid(10);

const toInt = (value, fallback) => {
  const n = Math.round(Number(value));
  return Number.isFinite(n) ? n : fallback;
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const str = (value, fallback = '') => {
  const s = value == null ? '' : String(value).trim();
  return s || fallback;
};

const nowIso = () => new Date().toISOString();

export const STARTER_PHASES = [
  { name: 'Discovery', color: 'teal' },
  { name: 'Design', color: 'purple' },
  { name: 'Build', color: 'blue' },
  { name: 'Release', color: 'green' }
];

export function totalSlots(roadmap) {
  return (roadmap?.settings?.sprintCount || DEFAULT_SPRINT_COUNT) * SLOTS_PER_SPRINT;
}

export function snapUnit(roadmap) {
  return roadmap?.settings?.halfSprints ? 1 : SLOTS_PER_SPRINT;
}

export function slotToSprint(slot) {
  return Math.floor(slot / SLOTS_PER_SPRINT) + 1;
}

export function formatRange(start, span) {
  const n = slotToSprint(start);
  if (span === 1) return `S${n}${start % 2 === 0 ? 'a' : 'b'}`;
  if (start % 2 === 0 && span === 2) return `S${n}`;
  const end = start + span;
  const from = start % 2 === 0 ? `S${n}` : `S${n}b`;
  const to = end % 2 === 0 ? `S${end / 2}` : `S${(end + 1) / 2}a`;
  return `${from} to ${to}`;
}

export function blockTouchesSprint(block, sprint) {
  const sprintStart = (sprint - 1) * SLOTS_PER_SPRINT;
  const sprintEnd = sprintStart + SLOTS_PER_SPRINT;
  return block.start < sprintEnd && block.start + block.span > sprintStart;
}

export function freeRange(project, slot, ignoreBlockId = null, total = Infinity) {
  let min = 0;
  let max = total;
  for (const b of project?.blocks || []) {
    if (b.id === ignoreBlockId) continue;
    const end = b.start + b.span;
    if (slot >= b.start && slot < end) return null;
    if (end <= slot) min = Math.max(min, end);
    if (b.start > slot) max = Math.min(max, b.start);
  }
  return { min, max };
}

export function blocksToSlots(blocks, total) {
  const slots = new Array(total).fill(null);
  for (const b of blocks || []) {
    for (let i = b.start; i < b.start + b.span && i < total; i++) {
      if (i >= 0) slots[i] = b.phaseId ?? '';
    }
  }
  return slots;
}

export function slotsToBlocks(slots) {
  const blocks = [];
  let i = 0;
  while (i < slots.length) {
    const value = slots[i];
    if (value === null || value === undefined) {
      i++;
      continue;
    }
    let j = i + 1;
    while (j < slots.length && slots[j] === value) j++;
    blocks.push({ id: uid(), phaseId: value || null, start: i, span: j - i });
    i = j;
  }
  return blocks;
}

export function makeMember(name, extra = {}) {
  return { id: uid(), name: str(name, 'Unnamed'), capacity: DEFAULT_CAPACITY, away: [], ...extra };
}

export function makePhase(name, color = 'neutral') {
  return { id: uid(), name: str(name, 'Untitled phase'), color: PHASE_COLOURS[color] ? color : 'neutral' };
}

export function makeProject({
  name,
  description = '',
  memberIds = [],
  blocks = [],
  group = '',
  status = '',
  links = [],
  dependsOn = [],
  allocation = {}
} = {}) {
  return {
    id: uid(),
    name: str(name, 'Untitled project'),
    description: String(description || ''),
    memberIds: [...memberIds],
    blocks: blocks.map((b) => ({ id: b.id || uid(), phaseId: b.phaseId ?? null, start: b.start, span: b.span })),
    group: str(group),
    status: STATUS_VALUES.has(status) ? status : '',
    links: sanitiseLinks(links),
    dependsOn: [...new Set(dependsOn)],
    allocation: { ...allocation }
  };
}

export function makeMilestone(name, slot, date = null) {
  return { id: uid(), name: str(name, 'Milestone'), slot, date: isIsoDay(date) ? date : null };
}

const SAFE_URL = /^(https?:\/\/|mailto:)/i;

export function safeUrl(value) {
  const url = str(value);
  if (!url) return '';
  if (SAFE_URL.test(url)) return url;
  // Bare domains like jira.example.com/browse/PAY-1 are treated as https.
  if (/^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(url)) return `https://${url}`;
  return '';
}

function linkLabel(url) {
  try {
    const u = new URL(url);
    return u.protocol === 'mailto:' ? u.pathname : u.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function sanitiseLinks(raw) {
  return (Array.isArray(raw) ? raw : [])
    .map((l) => (typeof l === 'string' ? { url: l } : l))
    .filter((l) => l && typeof l === 'object')
    .map((l) => {
      const url = safeUrl(l.url);
      return url ? { id: str(l.id) || uid(), label: str(l.label) || linkLabel(url), url } : null;
    })
    .filter(Boolean)
    .slice(0, 20);
}

export function createRoadmap({ name, members = [], phases = [], settings = {}, milestones = [] } = {}) {
  const now = nowIso();
  return sanitiseRoadmap({
    schemaVersion: SCHEMA_VERSION,
    id: uid(),
    name,
    createdAt: now,
    updatedAt: now,
    settings,
    members,
    phases,
    milestones,
    projects: []
  });
}

export function sanitiseSettings(raw) {
  return {
    sprintCount: clamp(toInt(raw?.sprintCount, DEFAULT_SPRINT_COUNT), MIN_SPRINT_COUNT, MAX_SPRINT_COUNT),
    halfSprints: Boolean(raw?.halfSprints),
    startDate: isIsoDay(raw?.startDate) ? raw.startDate : null,
    sprintLengthDays: clamp(toInt(raw?.sprintLengthDays, DEFAULT_SPRINT_LENGTH), MIN_SPRINT_LENGTH, MAX_SPRINT_LENGTH)
  };
}

const round2 = (n) => Math.round(n * 100) / 100;

export function sanitiseMilestones(raw, settings) {
  const total = settings.sprintCount * SLOTS_PER_SPRINT;
  return (Array.isArray(raw) ? raw : [])
    .filter((m) => m && typeof m === 'object' && str(m.name))
    .map((m) => {
      const date = isIsoDay(m.date) ? m.date : null;
      let slot = Number(m.slot);
      // A dated milestone follows its date whenever the roadmap has a start date.
      if (date && settings.startDate) slot = dateToSlot(settings, date);
      return { id: str(m.id) || uid(), name: str(m.name), slot: Number.isFinite(slot) ? round2(slot) : NaN, date };
    })
    .filter((m) => Number.isFinite(m.slot) && m.slot >= 0 && m.slot <= total)
    .sort((a, b) => a.slot - b.slot);
}

function sanitiseAllocation(raw, memberIds) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const id of memberIds) {
    if (raw[id] === undefined || raw[id] === null || raw[id] === '') continue;
    const n = toInt(raw[id], NaN);
    if (Number.isFinite(n)) out[id] = clamp(n, 0, 100);
  }
  return out;
}

function sanitisePlan(raw, settings) {
  const total = settings.sprintCount * SLOTS_PER_SPRINT;

  const members = (Array.isArray(raw?.members) ? raw.members : [])
    .map((m) => (typeof m === 'string' ? { name: m } : m))
    .filter((m) => m && str(m.name))
    .map((m) => ({
      id: str(m.id) || uid(),
      name: str(m.name),
      capacity: clamp(toInt(m.capacity, DEFAULT_CAPACITY), 0, 100),
      away: [...new Set((Array.isArray(m.away) ? m.away : []).map((n) => toInt(n, 0)))]
        .filter((n) => n >= 1 && n <= settings.sprintCount)
        .sort((a, b) => a - b)
    }));

  const phases = (Array.isArray(raw?.phases) ? raw.phases : [])
    .filter((p) => p && str(p.name))
    .map((p) => ({
      id: str(p.id) || uid(),
      name: str(p.name),
      color: PHASE_COLOURS[p.color] ? p.color : 'neutral'
    }));

  const memberIds = new Set(members.map((m) => m.id));

  const projects = (Array.isArray(raw?.projects) ? raw.projects : [])
    .filter(Boolean)
    .map((p) => {
      const ids = [...new Set((Array.isArray(p.memberIds) ? p.memberIds : []).filter((id) => memberIds.has(id)))];
      return {
        id: str(p.id) || uid(),
        name: str(p.name, 'Untitled project'),
        description: String(p.description || ''),
        memberIds: ids,
        blocks: (Array.isArray(p.blocks) ? p.blocks : [])
          .map((b) => {
            const start = Math.max(0, toInt(b?.start, 0));
            const span = Math.max(1, toInt(b?.span, SLOTS_PER_SPRINT));
            return { id: str(b?.id) || uid(), phaseId: b?.phaseId ? String(b.phaseId) : null, start, span };
          })
          .filter((b) => b.start < total)
          .map((b) => ({ ...b, span: Math.min(b.span, total - b.start) }))
          .sort((a, b) => a.start - b.start),
        group: str(p.group).slice(0, 80),
        status: STATUS_VALUES.has(p.status) ? p.status : '',
        links: sanitiseLinks(p.links),
        dependsOn: Array.isArray(p.dependsOn) ? p.dependsOn.map((id) => str(id)).filter(Boolean) : [],
        allocation: sanitiseAllocation(p.allocation, ids)
      };
    });

  const projectIds = new Set(projects.map((p) => p.id));
  for (const p of projects) p.dependsOn = [...new Set(p.dependsOn)].filter((id) => id !== p.id && projectIds.has(id));

  return { members, phases, projects, milestones: sanitiseMilestones(raw?.milestones, settings) };
}

function sanitiseBaselines(raw) {
  return (Array.isArray(raw) ? raw : [])
    .filter((b) => b && typeof b === 'object' && b.plan && typeof b.plan === 'object')
    .slice(-MAX_BASELINES)
    .map((b) => {
      const settings = sanitiseSettings(b.plan.settings);
      return {
        id: str(b.id) || uid(),
        name: str(b.name, 'Baseline'),
        createdAt: str(b.createdAt) || nowIso(),
        slotOffset: Math.max(0, toInt(b.slotOffset, 0)),
        plan: { settings, ...sanitisePlan(b.plan, settings) }
      };
    });
}

export function sanitiseRoadmap(raw) {
  const settings = sanitiseSettings(raw?.settings);
  return {
    schemaVersion: SCHEMA_VERSION,
    id: str(raw?.id) || uid(),
    name: str(raw?.name, 'Untitled roadmap'),
    createdAt: str(raw?.createdAt) || nowIso(),
    updatedAt: str(raw?.updatedAt) || nowIso(),
    settings,
    ...sanitisePlan(raw, settings),
    baselines: sanitiseBaselines(raw?.baselines),
    pinned: Boolean(raw?.pinned),
    archived: Boolean(raw?.archived)
  };
}

export function isLegacyRoadmap(raw) {
  if (!raw || typeof raw !== 'object') return false;
  // Anything written as v2 or later uses the block model, so only v1 shapes need migrating.
  if (Number(raw.schemaVersion) >= 2) return false;
  return Boolean(raw.data) || (Array.isArray(raw.projects) && raw.projects.some((p) => 'colStart' in (p || {})));
}

export function migrateLegacyRoadmap(raw) {
  const data = raw.data || raw;
  const legacyProjects = Array.isArray(data.projects) ? data.projects : [];

  const members = [];
  const memberByName = new Map();
  const ensureMember = (name) => {
    const key = str(name).toLowerCase();
    if (!key) return null;
    if (!memberByName.has(key)) {
      const m = makeMember(name);
      members.push(m);
      memberByName.set(key, m.id);
    }
    return memberByName.get(key);
  };
  for (const m of Array.isArray(data.members) ? data.members : []) ensureMember(typeof m === 'string' ? m : m?.name);

  const phases = [];
  const phaseByName = new Map();
  const ensurePhase = (name, theme) => {
    const key = str(name).toUpperCase();
    if (!key) return null;
    if (!phaseByName.has(key)) {
      const p = makePhase(name, theme);
      phases.push(p);
      phaseByName.set(key, p.id);
    }
    return phaseByName.get(key);
  };
  for (const p of Array.isArray(data.phases) ? data.phases : []) ensurePhase(p?.name, p?.theme || p?.color);

  const groups = new Map();
  for (const seg of legacyProjects) {
    if (!seg) continue;
    const key = seg.groupId || `legacy:${seg.row}:${str(seg.name)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(seg);
  }

  let maxSprint = 0;
  const projects = [...groups.values()]
    .map((segs) => {
      const row = Math.min(...segs.map((s) => toInt(s.row, 1)));
      const named = segs.find((s) => str(s.name));
      const described = segs.find((s) => str(s.description));
      const memberIds = new Set();
      for (const s of segs) for (const m of Array.isArray(s.members) ? s.members : []) {
        const id = ensureMember(m);
        if (id) memberIds.add(id);
      }
      const blocks = segs.map((s) => {
        let colStart = Math.max(1, toInt(s.colStart, 1));
        let colEnd = Math.max(colStart, toInt(s.colEnd, colStart));
        maxSprint = Math.max(maxSprint, colEnd);
        return {
          id: uid(),
          phaseId: ensurePhase(s.phase, 'neutral'),
          start: (colStart - 1) * SLOTS_PER_SPRINT,
          span: (colEnd - colStart + 1) * SLOTS_PER_SPRINT
        };
      });
      return {
        row,
        project: {
          id: uid(),
          name: named ? named.name : 'Untitled project',
          description: described ? described.description : '',
          memberIds: [...memberIds],
          blocks
        }
      };
    })
    .sort((a, b) => a.row - b.row)
    .map((x) => x.project);

  const declared = toInt(data.settings?.sprintCount, DEFAULT_SPRINT_COUNT);

  return sanitiseRoadmap({
    id: raw.id,
    name: raw.name,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    settings: { sprintCount: Math.max(declared, maxSprint), halfSprints: false },
    members,
    phases,
    projects
  });
}

export function normaliseRoadmap(raw) {
  if (!raw || typeof raw !== 'object') throw new Error('Not a roadmap');
  return isLegacyRoadmap(raw) ? migrateLegacyRoadmap(raw) : sanitiseRoadmap(raw);
}

export function extractRoadmaps(payload) {
  if (!payload || typeof payload !== 'object') return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.roadmaps)) return payload.roadmaps;
  if (payload.roadmap && typeof payload.roadmap === 'object') return [payload.roadmap];
  if ('name' in payload || 'data' in payload || 'projects' in payload) return [payload];
  return [];
}

function remapPlan(plan, memberMap, phaseMap, projectMap) {
  return {
    ...plan,
    members: (plan.members || []).map((m) => ({ ...m, id: memberMap.get(m.id) || uid() })),
    phases: (plan.phases || []).map((p) => ({ ...p, id: phaseMap.get(p.id) || uid() })),
    milestones: (plan.milestones || []).map((m) => ({ ...m, id: uid() })),
    projects: (plan.projects || []).map((p) => ({
      ...p,
      id: projectMap.get(p.id) || uid(),
      memberIds: p.memberIds.map((id) => memberMap.get(id)).filter(Boolean),
      allocation: Object.fromEntries(
        Object.entries(p.allocation || {})
          .map(([id, v]) => [memberMap.get(id), v])
          .filter(([id]) => id)
      ),
      dependsOn: (p.dependsOn || []).map((id) => projectMap.get(id)).filter(Boolean),
      links: (p.links || []).map((l) => ({ ...l, id: uid() })),
      blocks: p.blocks.map((b) => ({ ...b, id: uid(), phaseId: b.phaseId ? phaseMap.get(b.phaseId) ?? null : null }))
    }))
  };
}

export function cloneWithNewIds(roadmap, overrides = {}) {
  const memberMap = new Map();
  const phaseMap = new Map();
  const projectMap = new Map();
  const collect = (plan) => {
    for (const m of plan.members || []) if (!memberMap.has(m.id)) memberMap.set(m.id, uid());
    for (const p of plan.phases || []) if (!phaseMap.has(p.id)) phaseMap.set(p.id, uid());
    for (const p of plan.projects || []) if (!projectMap.has(p.id)) projectMap.set(p.id, uid());
  };
  collect(roadmap);
  for (const b of roadmap.baselines || []) collect(b.plan);
  const now = nowIso();
  return sanitiseRoadmap({
    ...remapPlan(roadmap, memberMap, phaseMap, projectMap),
    id: uid(),
    createdAt: now,
    updatedAt: now,
    pinned: false,
    baselines: (roadmap.baselines || []).map((b) => ({ ...b, id: uid(), plan: remapPlan(b.plan, memberMap, phaseMap, projectMap) })),
    ...overrides
  });
}

/** The parts of a roadmap that make up "the plan", used for undo history and baselines. */
export function planOf(roadmap) {
  const { settings, members, phases, projects, milestones } = roadmap;
  return JSON.parse(JSON.stringify({ settings, members, phases, projects, milestones }));
}

export function projectExtent(project) {
  if (!project?.blocks?.length) return null;
  let start = Infinity;
  let end = -Infinity;
  for (const b of project.blocks) {
    start = Math.min(start, b.start);
    end = Math.max(end, b.start + b.span);
  }
  return { start, end, span: end - start };
}

/** Projects that are planned to start before something they depend on has finished. */
export function dependencyIssues(roadmap) {
  const byId = new Map(roadmap.projects.map((p) => [p.id, p]));
  const issues = new Map();
  for (const p of roadmap.projects) {
    const mine = projectExtent(p);
    for (const depId of p.dependsOn || []) {
      const dep = byId.get(depId);
      const theirs = projectExtent(dep);
      if (!mine || !theirs || !dep) continue;
      if (mine.start < theirs.end) {
        if (!issues.has(p.id)) issues.set(p.id, []);
        issues.get(p.id).push({ project: dep, by: theirs.end - mine.start });
      }
    }
  }
  return issues;
}

export function allocationOf(project, memberId) {
  const v = project?.allocation?.[memberId];
  return Number.isFinite(v) ? v : DEFAULT_ALLOCATION;
}

export function fitMilestones(milestones, sprintCount) {
  const total = sprintCount * SLOTS_PER_SPRINT;
  const kept = (milestones || []).filter((m) => m.slot <= total);
  return { milestones: kept, removed: (milestones || []).length - kept.length };
}

/**
 * Move the timeline on by n sprints: the first n sprints drop off the front, n empty sprints
 * are added at the end, and the start date (if any) moves forward to match.
 */
export function rollForward(roadmap, n) {
  const count = clamp(toInt(n, 0), 0, roadmap.settings.sprintCount - 1);
  const shift = count * SLOTS_PER_SPRINT;
  if (!count) return { roadmap, dropped: 0 };
  let dropped = 0;
  const projects = roadmap.projects.map((p) => {
    const blocks = [];
    for (const b of p.blocks) {
      const end = b.start + b.span - shift;
      if (end <= 0) {
        dropped++;
        continue;
      }
      const start = Math.max(0, b.start - shift);
      blocks.push({ ...b, start, span: end - start });
    }
    return { ...p, blocks };
  });
  const settings = {
    ...roadmap.settings,
    startDate: roadmap.settings.startDate ? addDays(roadmap.settings.startDate, count * roadmap.settings.sprintLengthDays) : null
  };
  const milestones = (roadmap.milestones || [])
    .map((m) => ({ ...m, slot: m.slot - shift }))
    .filter((m) => m.slot >= 0);
  const members = roadmap.members.map((m) => ({ ...m, away: (m.away || []).map((s) => s - count).filter((s) => s >= 1) }));
  const baselines = (roadmap.baselines || []).map((b) => ({ ...b, slotOffset: (b.slotOffset || 0) + shift }));
  return { roadmap: { ...roadmap, settings, projects, milestones, members, baselines }, dropped };
}

export function fitBlocksToSettings(projects, { sprintCount, halfSprints }) {
  const total = sprintCount * SLOTS_PER_SPRINT;
  let trimmed = 0;
  let removed = 0;
  let snapped = 0;
  const next = projects.map((p) => {
    const blocks = [];
    for (const b of p.blocks) {
      let start = b.start;
      let end = b.start + b.span;
      if (!halfSprints) {
        const s = Math.floor(start / SLOTS_PER_SPRINT) * SLOTS_PER_SPRINT;
        const e = Math.ceil(end / SLOTS_PER_SPRINT) * SLOTS_PER_SPRINT;
        if (s !== start || e !== end) snapped++;
        start = s;
        end = e;
      }
      if (start >= total) {
        removed++;
        continue;
      }
      if (end > total) {
        trimmed++;
        end = total;
      }
      blocks.push({ ...b, start, span: end - start });
    }
    return { ...p, blocks: resolveOverlaps(blocks) };
  });
  return { projects: next, trimmed, removed, snapped };
}

export function resolveOverlaps(blocks) {
  const sorted = [...blocks].sort((a, b) => a.start - b.start);
  const out = [];
  for (const b of sorted) {
    const prev = out[out.length - 1];
    if (prev && b.start < prev.start + prev.span) {
      const end = b.start + b.span;
      const newStart = prev.start + prev.span;
      if (end <= newStart) continue;
      out.push({ ...b, start: newStart, span: end - newStart });
    } else {
      out.push(b);
    }
  }
  return out;
}
